// src/support/campaignParticipation.ts
//
// The Leader Campaigns tab's participation reports, derived from two server rollups:
//   - `db/campaign/participation-rollup/`: per-person facts (enrolled, journey-map counts, deliveries);
//   - `db/journey-map/map-rollup/`: per-journey-map progression.
// "People in scope" is everyone in the selected leader's org (the org chart's subtree), so a person with no
// activity at all still counts toward the denominators; the rollup omits them and absence means "no activity".

import { computeSubtreeScope, type OrgChartRow } from './orgScope';

export interface ParticipationPerson {
  email: string;
  enrolled: boolean;
  maps_assigned: number;
  maps_started: number;
  maps_completed: number;
  items_done: number;
  items_total: number;
  deliveries_open: number;
  deliveries_completed: number;
  deliveries_cancelled: number;
}

export interface ParticipationRollupResponse {
  campaign?: number;
  from?: string;
  to?: string;
  scope?: string;
  manager?: string;
  people: ParticipationPerson[];
}

export interface JourneyMapRollupRow {
  journey_map_id: number;
  name: string;
  /** "composite" is an enablement path made of other maps; its `children` are listed beneath it. */
  kind?: 'standard' | 'composite';
  children?: JourneyMapRollupRow[];
  items: number;
  assigned: number;
  not_started: number;
  in_progress: number;
  completed: number;
  /** Mean recorded progress, 0-100. */
  avg_progress: number;
  /** Mean days from assigned to completed; null when nothing is completed. */
  avg_days_to_complete: number | null;
  out_of_sync: number;
}

export interface JourneyMapRollupResponse {
  campaign?: number;
  scope?: string;
  manager?: string;
  maps: JourneyMapRollupRow[];
}

export type EnablementStage = 'not_assigned' | 'assigned_not_started' | 'in_progress' | 'completed';
export type DeliveryStage = 'none' | 'cancelled_only' | 'in_progress' | 'completed';
export type OverallStage =
  | 'no_activity'
  | 'enrolled_not_started'
  | 'enablement_in_progress'
  | 'enablement_completed'
  | 'delivery_in_progress'
  | 'delivery_completed';

const EMPTY: ParticipationPerson = {
  email: '',
  enrolled: false,
  maps_assigned: 0,
  maps_started: 0,
  maps_completed: 0,
  items_done: 0,
  items_total: 0,
  deliveries_open: 0,
  deliveries_completed: 0,
  deliveries_cancelled: 0,
};

/** One person's participation facts from a rollup; a person the rollup has no entry for has no activity. */
export function participationFor(
  response: ParticipationRollupResponse | null | undefined,
  email: string,
): ParticipationPerson {
  const key = String(email || '').toLowerCase();
  const found = (response?.people ?? []).find((p) => String(p.email || '').toLowerCase() === key);
  return found ?? { ...EMPTY, email: key };
}

/** A person's enablement stage: not assigned, assigned but not started, in progress, or every assigned map completed. */
export function enablementStage(p: ParticipationPerson): EnablementStage {
  if (p.maps_assigned <= 0) return 'not_assigned';
  if (p.maps_completed >= p.maps_assigned) return 'completed';
  return p.maps_started > 0 ? 'in_progress' : 'assigned_not_started';
}

/** Most advanced delivery status across a person's deliveries: completed > in progress > cancelled only > none. */
export function deliveryStage(p: ParticipationPerson): DeliveryStage {
  if (p.deliveries_completed > 0) return 'completed';
  if (p.deliveries_open > 0) return 'in_progress';
  if (p.deliveries_cancelled > 0) return 'cancelled_only';
  return 'none';
}

/**
 * The furthest stage a person has reached: delivery completed > delivery in progress > enablement completed >
 * enablement in progress > enrolled/assigned with no activity > no activity. A person whose only deliveries
 * were cancelled is placed by their enrollment and enablement instead.
 */
export function overallStage(p: ParticipationPerson): OverallStage {
  const delivery = deliveryStage(p);
  if (delivery === 'completed') return 'delivery_completed';
  if (delivery === 'in_progress') return 'delivery_in_progress';
  const enablement = enablementStage(p);
  if (enablement === 'completed') return 'enablement_completed';
  if (enablement === 'in_progress') return 'enablement_in_progress';
  if (p.enrolled || enablement === 'assigned_not_started') return 'enrolled_not_started';
  return 'no_activity';
}

export interface FunnelRow {
  key: string;
  label: string;
  people: number;
}

export const FUNNEL_LABELS = {
  enrolled: 'Enrolled in campaign',
  assigned: 'Assigned enablement',
  started: 'Started enablement',
  completed: 'Completed enablement (all assigned maps)',
  deliveryStarted: 'Started a delivery',
  deliveryCompleted: 'Completed a delivery',
} as const;

export const STAGE_LABELS: Record<OverallStage, string> = {
  no_activity: 'No activity',
  enrolled_not_started: 'Enrolled - not started',
  enablement_in_progress: 'Enablement in progress',
  enablement_completed: 'Enablement completed',
  delivery_in_progress: 'Delivery in progress',
  delivery_completed: 'Delivery completed',
};

export const PROGRESS_BUCKETS = ['Not started (0%)', '1% - 49%', '50% - 99%', '100% (complete)'] as const;

export interface ParticipationSummary {
  /** Everyone in the leader's org (the denominator for every percentage). */
  peopleInScope: number;
  funnel: FunnelRow[];
  /** Furthest stage reached, one row per stage in report order (zero-filled). */
  stages: FunnelRow[];
  /** People with assigned maps, bucketed by the share of their items done. */
  progressBuckets: FunnelRow[];
}

/** Items done as a percent of items total; a person with no items is 0. */
function itemsPercent(p: ParticipationPerson): number {
  return p.items_total > 0 ? (p.items_done / p.items_total) * 100 : 0;
}

/**
 * Funnel counts, furthest-stage breakdown and progress buckets for everyone in `manager`'s org. People the
 * rollup has no entry for are counted in scope with no activity. Anyone the rollup returned who is outside
 * the org chart's subtree (a stale or unmapped user) is ignored, so the figures can never exceed the org.
 */
export function summarizeParticipation(
  orgRows: OrgChartRow[],
  manager: string,
  response: ParticipationRollupResponse | null | undefined,
): ParticipationSummary {
  const inScope = computeSubtreeScope(orgRows, manager).emails.map((e) => e.toLowerCase());
  const byEmail = new Map<string, ParticipationPerson>();
  for (const p of response?.people ?? []) byEmail.set(String(p.email || '').toLowerCase(), p);

  const counts = {
    enrolled: 0,
    assigned: 0,
    started: 0,
    completed: 0,
    deliveryStarted: 0,
    deliveryCompleted: 0,
  };
  const stageCounts = new Map<OverallStage, number>();
  const buckets = [0, 0, 0, 0];

  for (const email of inScope) {
    const p = byEmail.get(email) ?? { ...EMPTY, email };
    if (p.enrolled) counts.enrolled += 1;
    if (p.maps_assigned > 0) counts.assigned += 1;
    if (p.maps_started > 0) counts.started += 1;
    if (enablementStage(p) === 'completed') counts.completed += 1;
    if (p.deliveries_open + p.deliveries_completed > 0) counts.deliveryStarted += 1;
    if (p.deliveries_completed > 0) counts.deliveryCompleted += 1;

    const stage = overallStage(p);
    stageCounts.set(stage, (stageCounts.get(stage) ?? 0) + 1);

    if (p.maps_assigned > 0) {
      const pct = itemsPercent(p);
      const i = pct >= 100 ? 3 : pct >= 50 ? 2 : pct > 0 ? 1 : 0;
      buckets[i] += 1;
    }
  }

  const funnel: FunnelRow[] = (Object.keys(FUNNEL_LABELS) as Array<keyof typeof FUNNEL_LABELS>).map((key) => ({
    key,
    label: FUNNEL_LABELS[key],
    people: counts[key],
  }));
  const stages: FunnelRow[] = (Object.keys(STAGE_LABELS) as OverallStage[]).map((key) => ({
    key,
    label: STAGE_LABELS[key],
    people: stageCounts.get(key) ?? 0,
  }));
  const progressBuckets: FunnelRow[] = PROGRESS_BUCKETS.map((label, i) => ({
    key: `bucket-${i}`,
    label,
    people: buckets[i],
  }));

  return { peopleInScope: inScope.length, funnel, stages, progressBuckets };
}

/** `count` as a whole-number percent of `total` (0 when there is no total). */
export function pctOf(count: number, total: number): number {
  return total > 0 ? Math.round((count / total) * 100) : 0;
}

export interface JourneyMapTotals {
  assigned: number;
  not_started: number;
  in_progress: number;
  completed: number;
  out_of_sync: number;
}

/** The total row: one count per configured map or path (a path's child rows are not added again). */
export function journeyMapTotals(maps: JourneyMapRollupRow[]): JourneyMapTotals {
  return maps.reduce<JourneyMapTotals>(
    (t, m) => ({
      assigned: t.assigned + m.assigned,
      not_started: t.not_started + m.not_started,
      in_progress: t.in_progress + m.in_progress,
      completed: t.completed + m.completed,
      out_of_sync: t.out_of_sync + m.out_of_sync,
    }),
    { assigned: 0, not_started: 0, in_progress: 0, completed: 0, out_of_sync: 0 },
  );
}
